<template>
  <section class="flex flex-col gap-3">
    <div class="rounded-lg border border-gray-800 bg-gray-900 overflow-hidden">
      <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-gray-800">
        <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">
          {{ $t('play.lan_party.your_match', { n: lobby.number }) }}
        </p>
        <p class="text-sm text-text-secondary flex items-center gap-2">
          <img v-if="lobby.cs_map" :src="pinUrl(lobby.cs_map.name)" alt="" class="w-5 h-5 object-contain" />
          {{ matchLabel }}
        </p>
      </div>
      <div class="px-5 py-5 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <div class="flex flex-col gap-2 min-w-0">
          <p class="text-sm font-semibold text-primary-300">{{ $t('play.lan_party.your_team') }}</p>
          <ul class="flex flex-col gap-1.5">
            <li v-for="m in mine" :key="m.user?.uuid" class="flex items-center gap-2 min-w-0">
              <LanAvatar :src="avatarOf(m)" :size="AVATAR" />
              <span class="text-sm text-text-primary truncate">{{ m.user?.username }}</span>
            </li>
          </ul>
        </div>
        <p class="text-4xl font-bold tabular-nums flex items-center gap-3">
          <template v-if="scores">
            <span class="text-primary-300">{{ scores[0] }}</span>
            <span class="text-gray-600">–</span>
            <span class="text-warning-400">{{ scores[1] }}</span>
          </template>
          <span v-else class="text-base font-semibold text-text-secondary">{{ statusLabel }}</span>
        </p>
        <div class="flex flex-col gap-2 items-end min-w-0">
          <p class="text-sm font-semibold text-warning-400">{{ $t('play.lan_party.chat_team') }}</p>
          <ul class="flex flex-col gap-1.5 items-end">
            <li v-for="m in theirs" :key="m.user?.uuid" class="flex flex-row-reverse items-center gap-2 min-w-0">
              <LanAvatar :src="avatarOf(m)" :size="AVATAR" />
              <span class="text-sm text-text-primary truncate">{{ m.user?.username }}</span>
            </li>
          </ul>
        </div>
      </div>
      <div class="px-5 pb-4 flex flex-wrap items-center justify-between gap-3">
        <p class="text-xs text-text-secondary">{{ $t('play.lan_party.badge_after_match') }}</p>
        <div class="flex items-center gap-2">
          <button
            type="button"
            :disabled="cancelling"
            @click="confirming = true"
            class="px-3 py-2 rounded-lg text-sm font-semibold text-red-300 hover:bg-red-950 disabled:opacity-50"
            style="background:#2a1215"
          >{{ $t('play.lan_party.cancel_match') }}</button>
          <router-link
            :to="{ name: 'MatchmakingLobbyPage', params: { id: lobby.uuid } }"
            class="px-4 py-2 rounded-lg bg-green-700 text-white text-sm font-semibold hover:bg-green-600"
          >{{ $t('play.matchmaking.go_to_matchroom') }}</router-link>
        </div>
      </div>
      <div
        v-if="confirming"
        class="mx-5 mb-4 rounded-lg border border-danger-800 bg-gray-950 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      >
        <p class="text-sm text-text-primary flex-1">{{ $t('play.lan_party.cancel_live_match_confirm') }}</p>
        <div class="flex gap-2">
          <button
            type="button"
            @click="confirming = false"
            class="px-3 py-2 rounded-lg bg-gray-800 text-text-primary text-sm font-semibold hover:bg-gray-700"
          >{{ $t('play.lan_party.keep_playing') }}</button>
          <button
            type="button"
            :disabled="cancelling"
            @click="cancel"
            class="px-3 py-2 rounded-lg bg-red-700 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-50"
          >{{ $t('play.lan_party.cancel_match') }}</button>
        </div>
      </div>
      <p v-if="error" class="px-5 pb-4 text-sm text-danger-400">{{ error }}</p>
    </div>
    <p class="text-xs text-text-secondary flex items-center gap-1.5">
      <LockClosedIcon class="w-3.5 h-3.5" aria-hidden="true" />{{ $t('play.lan_party.locked_during_match') }}
    </p>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { LockClosedIcon } from '@heroicons/vue/24/outline';
import LanAvatar from './LanAvatar.vue';
import { apiError, pinUrl } from '../../utils/lan_party';

const props = defineProps({
  lobby: { type: Object, required: true },
  managerUuids: { type: Array, default: () => [] },
  lanPartyUuid: { type: String, required: true },
});
const emit = defineEmits(['cancelled']);
const { t } = useI18n();

const confirming = ref(false);
const cancelling = ref(false);
const error = ref('');

// Cancelling shuts the server down and puts the viewers back in the LAN queue.
function cancel() {
  cancelling.value = true;
  error.value = '';
  axios.delete(`/matchmaking/lan_parties/${props.lanPartyUuid}/lobbies/${props.lobby.uuid}`).then(() => {
    confirming.value = false;
    emit('cancelled');
  }).catch((e) => {
    error.value = apiError(e, t('play.lan_party.network_error'));
  }).finally(() => {
    cancelling.value = false;
  });
}

const AVATAR = 'w-8 h-8 flex-shrink-0';
const matchLabel = computed(() => [
  props.lobby.play_mode?.friendly_name,
  props.lobby.cs_map?.friendly_name,
]
  .filter(Boolean)
  .join(' · '));

const teams = computed(() => props.lobby.teams || []);
// The Creator's side is the team holding a manager; the other one is chat.
const mineIndex = computed(() => {
  const i = teams.value.findIndex((team) => team
    .some((m) => props.managerUuids.includes(m.user?.uuid)));
  return i === -1 ? 0 : i;
});
const mine = computed(() => teams.value[mineIndex.value] || []);
const theirs = computed(() => teams.value.filter((_, i) => i !== mineIndex.value).flat());

function avatarOf(member) {
  return member.user?.avatarfull || member.user?.avatar || null;
}

function steamIds(list, key) {
  return new Set(list.map((x) => String(key(x) ?? '')).filter(Boolean));
}

// match_data sides swap at halftime, so pair them with the teams by steam ID.
const scores = computed(() => {
  const md = props.lobby.match_data;
  const sides = Array.isArray(md?.teams) ? md.teams : [md?.team_a, md?.team_b].filter(Boolean);
  if (sides.length < 2) return null;
  const myIds = steamIds(mine.value, (m) => m.user?.steam_id ?? m.user?.steamid);
  const overlap = (side) => (side.players || [])
    .filter((p) => myIds.has(String(p.steamid ?? p.steam_id))).length;
  const [a, b] = sides;
  const mineSide = overlap(b) > overlap(a) ? b : a;
  const otherSide = mineSide === a ? b : a;
  return [mineSide.score ?? 0, otherSide.score ?? 0];
});

const statusLabel = computed(() => (props.lobby.status === 'active'
  ? t('play.lan_party.match_live')
  : t('play.lan_party.match_starting')));
</script>
